import { useRef, type PointerEvent as ReactPointerEvent } from 'react';

interface UseSheetDragOptions {
  /** Estado actual del sheet: decide qué significa arrastrar hacia abajo. */
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  /** Arrastrar hacia abajo estando en "peek" cierra el sheet. */
  onDismiss?: () => void;
  /** Gesto sin desplazamiento: sigue siendo un toque que alterna. */
  onTap?: () => void;
}

/** Recorrido mínimo para que el gesto cuente como arrastre. */
const DRAG_THRESHOLD_PX = 32;
/** Por debajo de esto el dedo "no se movió": es un toque, no un arrastre. */
const TAP_SLOP_PX = 8;

/**
 * Gesto de arrastre vertical para un bottom-sheet.
 *
 * Devuelve props de Pointer Events para la zona agarrable (handle + header).
 * El estado se resuelve EN CUANTO se supera el umbral, no al soltar: la
 * transición de `max-height` del sheet hace de respuesta inmediata al dedo.
 *
 * Un elemento con `data-sheet-no-drag` dentro de la zona (el botón de cerrar)
 * queda fuera del gesto.
 */
export function useSheetDrag({ isExpanded, onExpand, onCollapse, onDismiss, onTap }: UseSheetDragOptions) {
  const startY = useRef<number | null>(null);
  const resolved = useRef(false);

  const handlePointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('[data-sheet-no-drag]')) return;
    startY.current = e.clientY;
    resolved.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (startY.current === null || resolved.current) return;
    const dy = e.clientY - startY.current;
    if (Math.abs(dy) < DRAG_THRESHOLD_PX) return;

    resolved.current = true;
    if (dy < 0) {
      if (!isExpanded) onExpand();
    } else if (isExpanded) {
      onCollapse();
    } else {
      onDismiss?.();
    }
  };

  const endGesture = (e: ReactPointerEvent<HTMLElement>) => {
    const start = startY.current;
    startY.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (start === null || resolved.current) return;
    if (Math.abs(e.clientY - start) <= TAP_SLOP_PX) onTap?.();
  };

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
  };
}
